import { useLocation, Link } from "@/lib/router-compat";
import { useEffect, useState } from "react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* ── SVG science molecules (branded, subtle) ── */
const MoleculeShape = ({ index }: { index: number }) => {
  const shapes = [
    // Hexagon (benzene ring)
    <svg key="hex" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <path d="M20 4L35 12V28L20 36L5 28V12L20 4Z" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <circle cx="20" cy="4" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="35" cy="12" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="5" cy="28" r="2" fill="currentColor" opacity="0.3" />
    </svg>,
    // DNA helix strand
    <svg key="dna" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <path d="M12 4C12 4 28 12 28 20C28 28 12 36 12 36" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <path d="M28 4C28 4 12 12 12 20C12 28 28 36 28 36" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <line x1="14" y1="12" x2="26" y2="12" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="13" y1="20" x2="27" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="14" y1="28" x2="26" y2="28" stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>,
    // Flask
    <svg key="flask" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <path d="M16 4V16L6 34H34L24 16V4" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <line x1="14" y1="4" x2="26" y2="4" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <path d="M10 28H30" stroke="currentColor" strokeWidth="1" opacity="0.3" strokeDasharray="2 2" />
    </svg>,
    // Molecule cluster
    <svg key="mol" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <circle cx="20" cy="20" r="5" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <circle cx="8" cy="10" r="3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <circle cx="32" cy="10" r="3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <circle cx="10" cy="32" r="3" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="15" y1="17" x2="10" y2="12" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="25" y1="17" x2="30" y2="12" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="17" y1="24" x2="12" y2="30" stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </svg>,
    // Capsule
    <svg key="cap" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <rect x="14" y="6" width="12" height="28" rx="6" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <line x1="14" y1="20" x2="26" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    </svg>,
    // Atom
    <svg key="atom" viewBox="0 0 40 40" fill="none" className="w-full h-full">
      <ellipse cx="20" cy="20" rx="16" ry="6" stroke="currentColor" strokeWidth="1" opacity="0.4" transform="rotate(0 20 20)" />
      <ellipse cx="20" cy="20" rx="16" ry="6" stroke="currentColor" strokeWidth="1" opacity="0.4" transform="rotate(60 20 20)" />
      <ellipse cx="20" cy="20" rx="16" ry="6" stroke="currentColor" strokeWidth="1" opacity="0.4" transform="rotate(120 20 20)" />
      <circle cx="20" cy="20" r="2.5" fill="currentColor" opacity="0.3" />
    </svg>,
  ];

  const shape = shapes[index % shapes.length];
  const left = `${10 + (index * 17) % 80}%`;
  const delay = index * 0.4;
  const duration = 6 + (index % 3) * 2;

  return (
    <motion.div
      className="absolute w-10 h-10 text-accent/30 select-none pointer-events-none"
      style={{ left }}
      initial={{ top: "110%", rotate: 0 }}
      animate={{ top: "-10%", rotate: 180 }}
      transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
    >
      {shape}
    </motion.div>
  );
};

const NotFound = () => {
  const location = useLocation();
  const [pillCount, setPillCount] = useState(0);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const messages = [
    "This page failed its clinical trial.",
    "Error 404: Not found in the dossier.",
    "This endpoint didn't pass regulatory review.",
    "Our Quality team has flagged this as a deviation.",
    "This page has been recalled from the market.",
  ];

  const clickMessages = [
    "That won't help. We've already filed a CAPA.",
    "Still 404. The auditor is not impressed.",
    "You're very persistent. Have you considered a career in QA?",
    "The more you click, the longer the corrective action takes.",
    "OK, we're escalating this to the QPPV.",
    "Critical finding: user won't stop clicking.",
  ];

  const mainMessage = messages[Math.floor(location.pathname.length % messages.length)];
  const clickMessage = pillCount > 0 ? clickMessages[Math.min(pillCount - 1, clickMessages.length - 1)] : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Page Not Found | QbD Group"
        description="The page you're looking for doesn't exist or has been moved."
        noIndex
      />
      <Navbar />
      <main className="flex-1 flex items-center justify-center relative overflow-hidden pt-40 pb-40 z-0">
        {/* Floating background SVG molecules */}
        {Array.from({ length: 6 }).map((_, i) => (
          <MoleculeShape key={i} index={i} />
        ))}

        <div className="text-center px-6 relative z-10 max-w-lg">
          {/* Giant 404 with capsule icon */}
          <motion.div
            className="mb-6"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <span className="text-[8rem] sm:text-[10rem] font-extrabold leading-none text-primary/10 select-none">
              4
            </span>
            <motion.button
              onClick={() => setPillCount((c) => c + 1)}
              className="relative text-[8rem] sm:text-[10rem] leading-none cursor-pointer focus:outline-none inline-flex items-center justify-center"
              whileHover={{ scale: 1.15, rotate: 15 }}
              whileTap={{ scale: 0.9, rotate: -15 }}
              title="Click the capsule!"
            >
              <svg viewBox="0 0 80 80" fill="none" className="w-[8rem] sm:w-[10rem] h-[8rem] sm:h-[10rem]">
                <rect x="24" y="10" width="32" height="60" rx="16" fill="var(--accent)" fillOpacity="0.15" stroke="var(--accent)" strokeWidth="2.5" />
                <line x1="24" y1="40" x2="56" y2="40" stroke="var(--accent)" strokeWidth="2" />
                <rect x="24" y="40" width="32" height="30" rx="0" fill="var(--accent)" fillOpacity="0.1" />
              </svg>
            </motion.button>
            <span className="text-[8rem] sm:text-[10rem] font-extrabold leading-none text-primary/10 select-none">
              4
            </span>
          </motion.div>

          {/* Main message */}
          <motion.h1
            className="text-xl sm:text-2xl font-bold text-foreground mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {mainMessage}
          </motion.h1>

          {/* Click feedback */}
          <div className="h-12 mb-6">
            {clickMessage && (
              <motion.p
                key={pillCount}
                className="text-sm text-muted-foreground italic"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {clickMessage}
              </motion.p>
            )}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 hover:shadow-lg transition-all duration-300"
            >
              Return to Compliance
            </Link>
            <p className="mt-4 text-xs text-muted-foreground">
              Attempted route: <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{location.pathname}</code>
            </p>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
